import { Dialog } from '@headlessui/react';
import { X, Loader2, CheckCircle, Copy, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { formatPrice, buildApiUrl, calculateCartDiscounts } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

// --- FIX START ---
// This component is now modified to handle the payment result directly without redirects.
function StripeCheckoutForm({
  total,
  storeInfo,
  isProcessing,
  setIsProcessing,
  customerEmail,
  onSuccessfulPayment, // New prop to signal success to the parent
}: {
  total: number;
  storeInfo: any;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
  customerEmail: string;
  onSuccessfulPayment: (paymentIntentId: string) => void; // Callback function
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error('Payment system not initialized');
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // We no longer need return_url as we handle the result here
          receipt_email: customerEmail,
        },
        // This is the key change: we prevent the automatic redirect
        redirect: 'if_required',
      });

      if (error) {
        // This can be a card error or if the user cancels the 3D secure authentication.
        toast.error(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      // If we are here, the payment was successful.
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast.success('Payment successful! Submitting order...');
        onSuccessfulPayment(paymentIntent.id); // Call the parent function with the payment ID
      } else {
        // This case handles payments that are still processing, etc.
        toast.error('Payment processing. We will notify you upon completion.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <PaymentElement />
      <Button type="submit" disabled={!stripe || isProcessing} className="w-full mt-4">
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Processing...
          </div>
        ) : (
          `Pay ${formatPrice(total, storeInfo?.currenCy)}`
        )}
      </Button>
    </form>
  );
}
// --- FIX END ---

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (paymentMethod: string, paymentReference?: string) => void; // Modified to pass data
  orderTotal: number;
  rawSubtotal: number;
  shippingTotal: number;
  vatAmount: number;
  customerEmail: string;
  customerName: string;
  storeInfo: any;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => void;
  orderPaymentReceipt: File | null;
  shippingPaymentReceipt: File | null;
  paymentMethod: 'bank' | 'paystack' | 'monnify' | 'stripe';
  onPaymentMethodChange: (method: 'bank' | 'paystack' | 'monnify' | 'stripe') => void;
  cart: any[];
  selectedCountry: string;
  selectedState: string;
  selectedLocation: any;
}

export function PaymentMethodDialog({
  open,
  onClose,
  onConfirm,
  orderTotal,
  rawSubtotal,
  shippingTotal,
  vatAmount,
  customerEmail,
  customerName,
  storeInfo,
  onFileUpload,
  orderPaymentReceipt,
  shippingPaymentReceipt,
  paymentMethod,
  onPaymentMethodChange,
  cart,
  selectedCountry,
  selectedState,
  selectedLocation,
}: PaymentMethodDialogProps) {
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState<string>('');
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const total = orderTotal + shippingTotal + vatAmount;
  
  // --- FIX START ---
  // Hooks must be called unconditionally at the top level of the component.
  useEffect(() => {
    // This effect is now just for detecting a successful state to show the UI
    if (location.state?.paymentStatus === 'success') {
      setPaymentSuccess(true);
      const timer = setTimeout(() => {
        // This onConfirm is for the old flow, we now call it directly from StripeCheckoutForm
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  // --- FIX END ---
  
  useEffect(() => {
    if (paymentMethod === 'stripe' && open) {
      setIsStripeLoading(true);
      const controller = new AbortController();

      // Send amount in smallest currency unit (cents) as integer to avoid fractional loss
      fetch(buildApiUrl('create-payment-intent.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          amount: Math.round(total * 100),
          email: customerEmail,
          domain: window.location.hostname,
          metadata: {
            customerName,
            customerEmail,
            country: selectedCountry,
            state: selectedState,
            shipping: selectedLocation?.name,
          },
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data && data.clientSecret && data.publishableKey) {
            setClientSecret(data.clientSecret);
            if (data.publishableKey !== publishableKey) {
              setPublishableKey(data.publishableKey);
              setStripePromise(loadStripe(data.publishableKey));
            }
          } else {
            throw new Error('Invalid Stripe response');
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Stripe init error', err);
            toast.error('Failed to initialize Stripe payment');
            onPaymentMethodChange('bank');
          }
        })
        .finally(() => setIsStripeLoading(false));

      return () => controller.abort();
    }
    setClientSecret('');
    setStripePromise(null);
    setPublishableKey(null);
  }, [paymentMethod, open, total, customerEmail, customerName, selectedCountry, selectedState, selectedLocation, onPaymentMethodChange, publishableKey]);

  // --- FIX START ---
  // This is the fix for the "Rendered more hooks" error.
  // The conditional return must happen AFTER all hooks have been called.
  if (!storeInfo) {
    return null;
  }
  // --- FIX END ---

  const { totalDiscount } = calculateCartDiscounts(cart);
  const showSeparateShipping = storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingTotal > 0;

  const handleCopyAccountNumber = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied to clipboard');
  };

  const handleConfirmPayment = async () => {
    if (paymentMethod === 'bank' && !orderPaymentReceipt) {
      toast.error('Please upload payment receipt');
      return;
    }
    if (paymentMethod === 'bank' && showSeparateShipping && !shippingPaymentReceipt) {
      toast.error('Please upload shipping payment receipt');
      return;
    }
    setIsProcessing(true);
    try {
      if (paymentMethod === 'paystack') {
        const handler = window.PaystackPop.setup({
          key: storeInfo?.pays_pk,
          email: customerEmail,
          amount: total * 100,
          currency: storeInfo?.currenCy || 'NGN',
          ref: `ord_${Math.floor(Math.random() * 1000000000)}`,
          callback: (response: any) => {
            if (response.status === 'success') onConfirm('paystack', response.reference);
            else {
              toast.error('Payment failed');
              setIsProcessing(false);
            }
          },
          onClose: () => {
            toast.error('Payment cancelled');
            setIsProcessing(false);
          },
        });
        handler.openIframe();
      } else if (paymentMethod === 'monnify') {
        const paymentRef = `ord_${Math.floor(Math.random() * 1000000000)}`;
        window.MonnifySDK.initialize({
          amount: total,
          currency: storeInfo?.currenCy || 'NGN',
          reference: paymentRef,
          customerName: customerName,
          customerEmail: customerEmail,
          apiKey: storeInfo?.monify_api,
          contractCode: storeInfo?.monify_contract,
          paymentDescription: 'Order Payment',
          isTestMode: true,
          metadata: { name: customerName },
          paymentMethods: ['CARD', 'ACCOUNT_TRANSFER'],
          onComplete: function (response: any) {
            if (response.status === 'SUCCESS') onConfirm('monnify', response.transactionReference);
            else toast.error('Payment failed');
            setIsProcessing(false);
          },
          onClose: function () {
            toast.error('Payment cancelled');
            setIsProcessing(false);
          },
        });
      } else {
        await onConfirm('bank');
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white dark:bg-gray-800">
          <div className="p-6 border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                {paymentSuccess ? 'Payment Successful' : 'Payment Method'}
              </Dialog.Title>
              {!paymentSuccess && !isProcessing && (
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-6 custom-scrollbar">
            {paymentSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Payment Successful!</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Your order is being processed...</p>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                   {/* Order Summary JSX remains the same */}
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Items Total:</span>
                      <span className="font-medium">{formatPrice(rawSubtotal)}</span>
                    </div>

                    {totalDiscount > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-green-600 dark:text-green-400">Discounts:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">-{formatPrice(totalDiscount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-300">Subtotal:</span>
                          <span className="font-medium">{formatPrice(orderTotal)}</span>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Shipping:</span>
                      <span className="font-medium">{formatPrice(shippingTotal, storeInfo?.currenCy)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">VAT:</span>
                      <span className="font-medium">{formatPrice(vatAmount, storeInfo?.currenCy)}</span>
                    </div>
                    <div className="pt-2 border-t dark:border-gray-600">
                      <div className="flex justify-between font-medium">
                        <span className="text-gray-900 dark:text-white">Total:</span>
                        <span className="text-gray-900 dark:text-white">{formatPrice(total, storeInfo?.currenCy)}</span>
                      </div>
                      {totalDiscount > 0 && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">You saved {formatPrice(totalDiscount)}!</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {storeInfo?.stripeActive === 1 && (
                    <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input type="radio" checked={paymentMethod === 'stripe'} onChange={() => onPaymentMethodChange('stripe')} className="mr-2" />
                        <span className="font-medium dark:text-white">Pay with Stripe</span>
                      </label>
                      {paymentMethod === 'stripe' && (
                        <div className="mt-4">
                          {isStripeLoading ? (
                            <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
                          ) : stripePromise && clientSecret ? (
                            <Elements stripe={stripePromise} options={{ clientSecret }}>
                              <StripeCheckoutForm
                                total={total}
                                storeInfo={storeInfo}
                                isProcessing={isProcessing}
                                setIsProcessing={setIsProcessing}
                                customerEmail={customerEmail}
                                onSuccessfulPayment={(paymentIntentId) => onConfirm('stripe', paymentIntentId)}
                              />
                            </Elements>
                          ) : (
                            <p className="text-sm text-red-600 dark:text-red-400">Failed to initialize payment. Please try again or choose a different payment method.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Other payment methods JSX remains the same */}
                  {storeInfo?.mb_active === 1 && (
                     <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input type="radio" checked={paymentMethod === 'bank'} onChange={() => onPaymentMethodChange('bank')} className="mr-2" />
                        <span className="font-medium dark:text-white">Bank Transfer</span>
                      </label>
                      {paymentMethod === 'bank' && (
                        <div className="mt-4 space-y-4">
                           {/* ... Bank Transfer details ... */}
                        </div>
                      )}
                    </div>
                  )}
                  {storeInfo?.pays_active === 1 && (
                     <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input type="radio" checked={paymentMethod === 'paystack'} onChange={() => onPaymentMethodChange('paystack')} className="mr-2" />
                        <span className="font-medium dark:text-white">Pay with Paystack</span>
                      </label>
                    </div>
                  )}
                   {storeInfo?.monify_active === 1 && (
                     <div className="p-4 border rounded-lg dark:border-gray-700">
                      <label className="flex items-center">
                        <input type="radio" checked={paymentMethod === 'monnify'} onChange={() => onPaymentMethodChange('monnify')} className="mr-2" />
                        <span className="font-medium dark:text-white">Pay with Monnify</span>
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          {!paymentSuccess && paymentMethod !== 'stripe' && (
            <div className="p-6 border-t dark:border-gray-700">
              <div className="flex justify-end space-x-4">
                <Button variant="outline" onClick={onClose} disabled={isProcessing}>Cancel</Button>
                <Button onClick={handleConfirmPayment} disabled={isProcessing}>
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                    </>
                  ) : (
                    `Pay ${formatPrice(total, storeInfo?.currenCy)}`
                  )}
                </Button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}